<template>
  <!-- Proxies page for ProProxyUI servers: one port = HTTP + SOCKS5, bound to one modem -->
  <div class="pu">
    <div class="pu-table">
      <div class="pu-row pu-row--head">
        <span></span><span>Server</span><span>Modem</span><span>Port</span><span>Login / whitelist</span><span>Public IP</span><span>Status</span><span>VPN</span><span>Sold to</span><span>OS</span><span>Today</span><span></span>
      </div>
      <div v-for="p in rows" :key="p.uuid" class="pu-row" :class="{ 'is-off': p.active === 'inactive' }">
        <span><input type="checkbox" class="form-check-input shadow-none" :checked="selected.includes(p.uuid)" @change="toggle(p.uuid)" /></span>
        <span class="pu-two"><b>{{ p.modem }}</b><small v-if="p.active === 'inactive'">not for sale</small></span>
        <span class="pu-two"><span>{{ modemLabel(p) }}</span><small>…{{ String(p.imei).slice(-4) }}</small>
          <small v-if="p.modem_match === 'unmatched'" class="pu-imei-bad">IMEI not found on server{{ p.imei_valid === false ? ' · IMEI looks mistyped' : '' }}</small></span>
        <span class="pu-two"><span class="pu-mono">{{ p.http_port }}</span><small v-if="p.sock_port && p.sock_port !== p.http_port">SOCKS5 {{ p.sock_port }}</small></span>
        <span class="pu-two"><span class="pu-mono">{{ p.auth_user || '—' }}</span><small v-if="p.auth_ips">{{ p.auth_ips }}</small></span>
        <span class="pu-two"><span class="pu-mono">{{ p.public_ip || '—' }}</span><small v-if="p.ip_mode && p.ip_mode !== 'ipv4'">{{ IP_LABEL[p.ip_mode] }} · {{ p.public_ipv6 || 'no IPv6 yet' }}</small></span>
        <span><span class="pu-pill" :class="p.status === 'CONNECTED' ? 'is-on' : 'is-bad'">{{ p.status === 'CONNECTED' ? 'Online' : 'Offline' }}</span></span>
        <span class="pu-two pu-vpn">
          <template v-if="vpnStates[p.uuid]">
            <span><i class="pu-dot" :class="{ 'is-on': vpnStates[p.uuid].wg_connected }"></i>WireGuard<small v-if="vpnStates[p.uuid].wg_connected && vpnStates[p.uuid].wg_seen"> · {{ ago(vpnStates[p.uuid].wg_seen) }}</small>
              <button type="button" class="pu-dl" title="Download the WireGuard file" @click="downloadVpn(p, 'wg')"><v-icon icon="mdi-download" size="13"></v-icon></button></span>
            <span><i class="pu-dot" :class="{ 'is-on': vpnStates[p.uuid].ovpn_connected }"></i>OpenVPN<small v-if="vpnStates[p.uuid].ovpn_sessions"> · {{ vpnStates[p.uuid].ovpn_sessions }} session{{ vpnStates[p.uuid].ovpn_sessions === 1 ? '' : 's' }}</small>
              <button type="button" class="pu-dl" title="Download the OpenVPN file" @click="downloadVpn(p, 'ovpn')"><v-icon icon="mdi-download" size="13"></v-icon></button></span>
          </template>
          <small v-else>—</small>
        </span>
        <span>{{ p.usage_status === 'AVAILABLE' ? 'Free' : (p.customer_name || 'Sold') }}</span>
        <span>{{ osLabel(p.os_profile) }}</span>
        <span class="pu-mono">{{ p.usage_today_mb }} MB</span>
        <span class="text-right">
          <v-menu location="bottom end">
            <template v-slot:activator="{ props }"><v-btn v-bind="props" density="compact" size="small" class="bg-dark text-light">Actions</v-btn></template>
            <v-list density="compact">
              <v-list-item @click="openEdit(p)">Edit</v-list-item>
              <v-list-item @click="modemAction(p, 'rotate')">Rotate IP</v-list-item>
              <v-list-item @click="modemAction(p, 'reboot')">Reboot modem</v-list-item>
              <v-list-item @click="modemAction(p, 'usbreset')">USB reset</v-list-item>
              <v-list-item @click="toggleActive(p)">{{ p.active === 'inactive' ? 'Put back on sale' : 'Take off sale' }}</v-list-item>
            </v-list>
          </v-menu>
        </span>
      </div>
      <p v-if="!rows.length" class="pu-empty">No ProProxyUI ports{{ selectedServerName && selectedServerName !== 'All' ? ' on this server' : '' }} yet.</p>
    </div>

    <v-dialog v-model="dialog" max-width="560">
      <div class="pu-dlg">
        <div class="pu-dlg__head"><span>{{ form.uuid ? `Edit port ${form.listen_port}` : `Add port · ${selectedServerName}` }}</span><button type="button" aria-label="Close" @click="dialog = false">&times;</button></div>
        <div class="pu-dlg__body">
          <p v-if="boxError" class="pu-err">{{ boxError }}</p>
          <div><label>Modem</label>
            <v-select v-model="form.imei" :items="modemItems" item-title="label" item-value="imei" variant="outlined" density="compact" hide-details rounded="0" :loading="boxLoading"></v-select></div>
          <p v-if="sharedWarning" class="pu-warn">This modem already has {{ sharedWarning.n }} port{{ sharedWarning.n === 1 ? '' : 's' }}{{ sharedWarning.twin ? ` (${sharedWarning.twin} on ${twinName})` : '' }}{{ sharedWarning.other ? ` (${sharedWarning.other} from another dashboard on this box)` : '' }}. Customers on the same modem share its IP and rotations.</p>
          <div class="grid grid-cols-2 gap-3">
            <div><label>Port</label><v-text-field v-model.number="form.listen_port" type="number" variant="outlined" density="compact" hide-details rounded="0" :disabled="!!form.uuid" :placeholder="nextPort ? String(nextPort) : 'next free'"></v-text-field></div>
            <div><label>Login (user:password)</label><v-text-field v-model="form.auth_user" variant="outlined" density="compact" hide-details rounded="0" placeholder="random" class="pu-mono-input"></v-text-field></div>
          </div>
          <!-- boxes with a second (SOCKS5) port only: the box default, or a custom number (box altPort) -->
          <div v-if="altOffset" class="grid grid-cols-3 gap-3">
            <div><label>SOCKS5 port</label>
              <v-text-field v-if="form.alt_mode === 'custom'" v-model.number="form.alt_port" type="number" variant="outlined" density="compact" hide-details rounded="0"></v-text-field>
              <v-text-field v-else :model-value="defaultSocks || ''" disabled variant="outlined" density="compact" hide-details rounded="0"></v-text-field></div>
            <div class="col-span-2 flex flex-col justify-end gap-1">
              <div class="pu-seg"><button type="button" :class="{ on: form.alt_mode !== 'custom' }" @click="form.alt_mode = 'default'">Box default</button><button type="button" :class="{ on: form.alt_mode === 'custom' }" @click="useCustomSocks">Custom</button></div>
              <small class="pu-note">{{ form.alt_mode === 'custom' ? 'A number of your own for this port' : `HTTP ${altOffset > 0 ? '+' : '−'} ${Math.abs(altOffset)} on this box` }}</small>
            </div>
          </div>
          <div><label>IP whitelist</label><v-text-field v-model="form.auth_ips" variant="outlined" density="compact" hide-details rounded="0" placeholder="none"></v-text-field></div>
          <div class="grid grid-cols-3 gap-3">
            <div><label>IP version</label><v-select v-model="form.ip_mode" :items="ipItems" item-title="name" item-value="value" variant="outlined" density="compact" hide-details rounded="0"></v-select></div>
            <div class="col-span-2 flex flex-col justify-end"><small class="pu-note">{{ ipNote }}</small><small v-if="ipMismatch" class="pu-note pu-imei-bad">{{ ipMismatch }}</small></div>
          </div>
          <div class="grid grid-cols-3 gap-3">
            <div><label>OS fingerprint</label><v-select v-model="form.os_profile" :items="OS_ITEMS" item-title="label" item-value="value" variant="outlined" density="compact" hide-details rounded="0"></v-select></div>
            <div><label>Speed limit (Mbps)</label><v-text-field v-model.number="form.limit_mbps" type="number" min="0" variant="outlined" density="compact" hide-details rounded="0" placeholder="0 = none"></v-text-field></div>
            <div><label>Daily data (GB)</label><v-text-field v-model.number="form.daily_gb" type="number" min="0" variant="outlined" density="compact" hide-details rounded="0" placeholder="0 = none"></v-text-field></div>
          </div>
          <div class="grid grid-cols-3 gap-3">
            <div><label>Max connections</label><v-text-field v-model.number="form.max_conns" type="number" min="0" variant="outlined" density="compact" hide-details rounded="0" placeholder="0 = none"></v-text-field></div>
            <div><label>Auto-rotate (min)</label><v-text-field v-model.number="form.auto_rotate_min" type="number" min="0" variant="outlined" density="compact" hide-details rounded="0" placeholder="0 = off"></v-text-field></div>
            <div><label>MTU seen by sites</label><v-select v-model="form.mtu" :items="mtuItems" item-title="name" item-value="value" variant="outlined" density="compact" hide-details rounded="0"></v-select>
              <small class="pu-note">{{ mtuNote }}</small>
              <a v-if="chosenModem" href="#" class="pu-note pu-link" @click.prevent="recheckMtu">{{ mtuChecking ? 'Checking…' : 'Re-check MTU' }}</a></div>
          </div>
        </div>
        <div class="pu-dlg__foot">
          <button type="button" class="pu-btn" @click="dialog = false">Cancel</button>
          <button type="button" class="pu-btn pu-btn--pri" :disabled="busy || !form.imei" @click="save">{{ busy ? 'Saving…' : form.uuid ? 'Save' : 'Create port' }}</button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount, defineProps, defineExpose } from 'vue';
import { storeToRefs } from 'pinia';
import { fetchWrapper } from '@/helpers';
import { useAdminProxyStore, useAlertStore } from '@/stores';
import { regionOf } from '@/helpers/region';

const props = defineProps({ selectedServerName: String, selectedSaleStatus: String, selectedConnStatus: String, selectedRegion: { type: String, default: 'all' } });
const API = `${process.env.VUE_APP_VUE_API_URL}`;
const store = useAdminProxyStore();
const { ppuiProxies } = storeToRefs(store);
const alerts = useAlertStore();
const OS_ITEMS = [
  { value: '', label: 'Native' }, { value: 'windows', label: 'Windows' }, { value: 'windows-old', label: 'Windows (old)' }, { value: 'macos', label: 'macOS' },
  { value: 'ios', label: 'iOS' }, { value: 'android', label: 'Android' }, { value: 'android-old', label: 'Android (old)' }, { value: 'linux', label: 'Linux' }, { value: 'ubuntu24', label: 'Ubuntu 24' },
];
const osLabel = (v) => (OS_ITEMS.find((o) => o.value === (v || '')) || { label: v }).label;

const rows = computed(() => (ppuiProxies.value || []).filter((p) =>
  (props.selectedRegion === 'all' || regionOf(p.modem_country) === props.selectedRegion)
  && (!props.selectedSaleStatus || props.selectedSaleStatus === 'All' || p.usage_status === props.selectedSaleStatus)
  && (!props.selectedConnStatus || props.selectedConnStatus === 'All' || (props.selectedConnStatus === 'PAUSED' ? p.active === 'inactive' : p.status === props.selectedConnStatus))));

const selected = ref([]);
const toggle = (uuid) => { selected.value = selected.value.includes(uuid) ? selected.value.filter((u) => u !== uuid) : [...selected.value, uuid]; };
const reload = () => store.loadProxies(props.selectedServerName || 'All');

// modem names from the last box read (Add / Edit dialog), falling back to the IMEI
const boxModems = ref([]);
const modemLabel = (p) => (boxModems.value.find((m) => String(m.imei) === String(p.imei)) || {}).nickname || 'Modem';

const dialog = ref(false);
const busy = ref(false);
const boxLoading = ref(false);
const boxError = ref('');
const nextPort = ref(null);
const blank = () => ({ uuid: null, imei: null, listen_port: null, auth_user: '', auth_ips: '', os_profile: '', limit_mbps: 0, daily_gb: 0, max_conns: 0, auto_rotate_min: 0, mtu: 0, ip_mode: 'ipv4', alt_mode: 'default', alt_port: null, modem: null });
const form = ref(blank());
// ports per modem as the box counts them (ours and other dashboards'), modems without one first, like the ProxySmart window
const boxCount = (m) => (m.box_ports !== undefined ? m.box_ports : m.ports) || 0;
const modemItems = computed(() => [...boxModems.value]
  .sort((a, b) => (boxCount(a) - boxCount(b)) || String(a.nickname).localeCompare(String(b.nickname)))
  .map((m) => {
    const n = boxCount(m);
    const used = n ? ` · ${n} port${n === 1 ? '' : 's'}${m.twin_ports ? ` (${m.twin_ports} on ${twinName.value})` : ''}${m.other_ports ? ` (${m.other_ports} from another dashboard)` : ''}` : '';
    return { imei: m.imei, label: `${m.nickname} · ${m.state}${m.public_ip ? ' · ' + m.public_ip : ''}${used}` };
  }));
// MTU: match the fingerprint, or one of the values the box accepts for the chosen modem (mtuChoices of its box ports)
const chosenModem = computed(() => boxModems.value.find((m) => String(m.imei) === String(form.value.imei)) || null);
const mtuItems = computed(() => {
  const list = [{ value: 0, name: 'Match fingerprint (default)' }, ...((chosenModem.value && chosenModem.value.mtu_choices) || [])];
  const cur = Number(form.value.mtu) || 0;
  if (cur && !list.some((p) => p.value === cur)) list.push({ value: cur, name: `${cur} (not accepted by this modem now)` });
  return list;
});
const mtuNote = computed(() => {
  const m = chosenModem.value;
  if (!m || !m.mtu_choices) return 'Choices show once this modem has a port on the box, or after a re-check';
  const when = m.mtu_measured_at ? ` · measured ${ago(m.mtu_measured_at)}` : '';
  if (!m.carrier_mtu) return 'Carrier MTU not measured yet: no cap for now';
  return (m.mtu_choices.length ? `Carrier max ${m.carrier_mtu}` : `Carrier max ${m.carrier_mtu}: below 1360, default only`) + when;
});
// the box measures the chosen modem now (admin only; the backend allows once a minute per modem)
const boxServer = ref('');
const mtuChecking = ref(false);
const recheckMtu = async () => {
  const m = chosenModem.value;
  if (!m || mtuChecking.value) return;
  mtuChecking.value = true;
  try {
    const r = await fetchWrapper.post(`${API}/admin/proproxyui/mtu-check`, { modem_name: boxServer.value, imei: m.imei });
    Object.assign(m, { carrier_mtu: r.carrier_mtu, mtu_choices: r.mtu_choices || [], mtu_measured_at: r.measured_at });
    alerts.success(r.carrier_mtu ? `Carrier MTU ${r.carrier_mtu}` : 'Carrier MTU could not be measured: no cap for now');
  } catch (e) { alerts.error(e); }
  mtuChecking.value = false;
};
const sharedWarning = computed(() => {
  const m = boxModems.value.find((x) => String(x.imei) === String(form.value.imei));
  if (!m) return null;
  const n = boxCount(m) - (form.value.uuid && String(form.value.originalImei) === String(m.imei) ? 1 : 0);
  return n > 0 ? { n, other: m.other_ports || 0, twin: m.twin_ports || 0 } : null;
});
// IP version (box ipMode). IPv6 and Dual are offered when the modem has an IPv6 (or the port already uses them).
const IP_LABEL = { ipv4: 'IPv4', ipv6: 'IPv6', dual: 'Dual' };
// the box's second (SOCKS5) port: HTTP + altOffset (0 = one port for both, no SOCKS5 field)
const altOffset = ref(0);
const defaultSocks = computed(() => { const h = Number(form.value.listen_port || nextPort.value) || 0; return h && altOffset.value ? h + altOffset.value : null; });
const useCustomSocks = () => { if (!form.value.alt_port) form.value.alt_port = defaultSocks.value; form.value.alt_mode = 'custom'; };
const serverIpv6 = ref(false);
const twinName = ref('');
const ipItems = computed(() => {
  const m = chosenModem.value, cur = form.value.ip_mode;
  const v6 = !!(m && m.public_ipv6);
  return [{ value: 'ipv4', name: 'IPv4' },
    ...(v6 || cur === 'dual' ? [{ value: 'dual', name: 'Dual (IPv6 first, IPv4 fallback)' }] : []),
    ...(v6 || cur === 'ipv6' ? [{ value: 'ipv6', name: 'IPv6 only' }] : [])];
});
const ipNote = computed(() => {
  const m = chosenModem.value;
  if (!m) return '';
  if (!m.public_ipv6) return 'This modem has no IPv6 right now, so only IPv4 is offered.';
  return `Modem IPv6 ${m.public_ipv6}${m.ipv6_only ? ' · IPv6-only SIM: IPv4 sites are reached through the carrier NAT64' : ''}`;
});
const ipMismatch = computed(() => {
  const mode = form.value.ip_mode || 'ipv4';
  if (serverIpv6.value && mode === 'ipv4') return 'This server is sold as IPv6: choose Dual or IPv6.';
  if (!serverIpv6.value && mode !== 'ipv4') return 'This server is sold as IPv4: its IPv6 copy sells IPv6 ports.';
  return '';
});
// a new port: Dual on the IPv6 copy of a box when the modem has an IPv6, otherwise IPv4
watch(() => [form.value.imei, boxModems.value], () => {
  if (form.value.uuid || !dialog.value) return;
  const m = chosenModem.value;
  form.value.ip_mode = serverIpv6.value && m && m.public_ipv6 ? 'dual' : 'ipv4';
});
const loadBox = async (serverName) => {
  boxLoading.value = true; boxError.value = ''; boxServer.value = serverName;
  try {
    const r = await fetchWrapper.post(`${API}/admin/proproxyui/box`, { modem_name: serverName });
    boxModems.value = r.modems || []; nextPort.value = r.next_port; serverIpv6.value = !!r.server_ipv6; twinName.value = r.twin_name || ''; altOffset.value = Number(r.alt_offset) || 0;
  } catch (e) { boxError.value = typeof e === 'string' ? e : 'The box did not answer'; boxModems.value = []; }
  boxLoading.value = false;
};
// modem names for the table when one server is shown
watch(() => props.selectedServerName, (name) => { if (name && name !== 'All' && !dialog.value) loadBox(name); }, { immediate: true });
const openAdd = async () => {
  if (!props.selectedServerName || props.selectedServerName === 'All') { alerts.error('Choose a ProProxyUI server first'); return; }
  form.value = blank(); dialog.value = true;
  await loadBox(props.selectedServerName);
  const free = boxModems.value.find((m) => !boxCount(m) && m.state === 'online') || boxModems.value.find((m) => !boxCount(m)) || boxModems.value[0];
  if (free) form.value.imei = free.imei;
};
const openEdit = async (p) => {
  form.value = { uuid: p.uuid, imei: p.imei, originalImei: p.imei, listen_port: p.http_port, auth_user: p.auth_user || '', auth_ips: p.auth_ips || '', os_profile: p.os_profile || '',
    limit_mbps: p.limit_mbps || 0, daily_gb: p.daily_gb || 0, max_conns: p.max_conns || 0, auto_rotate_min: p.auto_rotate_min || 0, mtu: p.mtu || 0, ip_mode: p.ip_mode || 'ipv4', modem: p.modem };
  dialog.value = true;
  await loadBox(p.modem);
  // a custom SOCKS5 number: set here before, or a SOCKS5 port that is not HTTP + the box offset (e.g. moved from ProxySmart)
  const custom = Number(p.alt_port) || (altOffset.value && p.sock_port && Number(p.sock_port) !== Number(p.http_port) + altOffset.value ? Number(p.sock_port) : 0);
  form.value.alt_mode = custom ? 'custom' : 'default'; form.value.alt_port = custom || null;
};
const save = async () => {
  const f = form.value;
  const body = { auth_user: f.auth_user.trim(), auth_ips: f.auth_ips, os_profile: f.os_profile, limit_mbps: f.limit_mbps || 0, daily_gb: f.daily_gb || 0, max_conns: f.max_conns || 0, auto_rotate_min: f.auto_rotate_min || 0, mtu: f.mtu || 0, ip_mode: f.ip_mode || 'ipv4', imei: f.imei,
    ...(altOffset.value ? { alt_port: f.alt_mode === 'custom' ? Number(f.alt_port) || 0 : 0 } : {}) };
  busy.value = true;
  try {
    if (f.uuid) {
      await fetchWrapper.post(`${API}/admin/proproxyui/update-port`, { uuid: f.uuid, ...body });
      alerts.success('Port saved');
    } else {
      const r = await fetchWrapper.post(`${API}/admin/proproxyui/create-port`, { modem_name: props.selectedServerName, listen_port: f.listen_port || undefined, ...body, auth_user: body.auth_user || undefined });
      alerts.success(`Port ${r.listen_port} created`);
    }
    dialog.value = false;
    await reload();
  } catch (e) { alerts.error(e); }
  busy.value = false;
};
const modemAction = async (p, action) => {
  const what = { rotate: 'Rotate the IP of', reboot: 'Reboot', usbreset: 'USB reset' }[action];
  if (!confirm(`${what} the modem behind port ${p.http_port}? Every port on that modem drops for a moment.`)) return;
  try {
    const r = await fetchWrapper.post(`${API}/admin/proproxyui/modem-action`, { uuid: p.uuid, action });
    alerts.success(action === 'rotate' && r.result ? `New IP ${r.result.newIp}${r.result.changed === false ? ' (the carrier gave the same IP)' : ''}` : 'Done');
    await reload();
  } catch (e) { alerts.error(e); }
};
const toggleActive = async (p) => {
  try { await fetchWrapper.post(`${API}/admin/proproxyui/toggle-active`, { uuid: p.uuid }); await reload(); } catch (e) { alerts.error(e); }
};
const deleteProxies = async () => {
  if (!selected.value.length) { alerts.error('Tick the ports to delete'); return; }
  if (!confirm(`Delete ${selected.value.length} port(s) from the box? Sold ports are skipped.`)) return;
  try {
    const r = await fetchWrapper.post(`${API}/admin/proproxyui/delete-ports`, { uuids: selected.value });
    alerts.success(r.message === 'success' ? 'Deleted' : r.message);
    selected.value = [];
    await reload();
  } catch (e) { alerts.error(e); }
};
// live VPN state (the box keeps it in memory only), every 30 s while this page is open
const vpnStates = ref({});
const loadVpn = async () => {
  if (document.hidden) return;
  try { const r = await fetchWrapper.post(`${API}/admin/proproxyui/vpn-status`, { modem_name: props.selectedServerName || 'All' }); vpnStates.value = r.states || {}; } catch (e) { /* the column shows a dash until the next try */ }
};
let vpnTimer = null;
onMounted(() => { loadVpn(); vpnTimer = setInterval(loadVpn, 30000); });
onBeforeUnmount(() => clearInterval(vpnTimer));
watch(() => props.selectedServerName, loadVpn);
const ago = (iso) => {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (!Number.isFinite(s)) return '';
  return s < 60 ? `${s} s ago` : s < 3600 ? `${Math.round(s / 60)} min ago` : `${Math.round(s / 3600)} h ago`;
};
// the port's WireGuard / OpenVPN file (a sold port: its customer's)
const downloadVpn = async (p, type) => {
  try {
    const f = await fetchWrapper.post(`${API}/admin/proproxyui/vpn-file`, { port_uuid: p.uuid, type });
    if (!f || !f.content) return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([f.content], { type: 'application/octet-stream' }));
    a.download = f.filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  } catch (e) { alerts.error(e); }
};
defineExpose({ openAdd, deleteProxies });
</script>

<style scoped>
.pu-table { border: 1px solid var(--ui-line); border-radius: 12px; background: var(--ui-card); overflow-x: auto; }
.pu-row { display: grid; grid-template-columns: 28px minmax(110px, 1.1fr) minmax(110px, 1.1fr) 70px minmax(150px, 1.4fr) 120px 80px 200px minmax(90px, 1fr) 90px 90px 90px; gap: 10px; align-items: center; padding: 8px 12px; border-bottom: 1px solid var(--ui-line); min-width: 1280px; font-size: 13px; }
.pu-vpn { font-size: 12px; white-space: nowrap; }
.pu-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 5px; background: #ef4444; }
.pu-dot.is-on { background: #22c55e; }
.pu-dl { margin-left: 4px; padding: 0 3px; border: 1px solid #22c55e; border-radius: 5px; color: #22c55e; background: transparent; line-height: 14px; cursor: pointer; }
.pu-dl:hover { background: rgba(34, 197, 94, 0.12); }
.pu-row--head { color: var(--ui-dim); font-size: 12px; }
.pu-row.is-off { opacity: 0.6; }
.pu-two { display: flex; flex-direction: column; min-width: 0; }
.pu-two small { color: var(--ui-dim); font-size: 11.5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pu-mono { font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 12px; overflow-wrap: anywhere; }
.pu-pill { display: inline-block; padding: 1px 9px; border-radius: 999px; font-size: 11.5px; font-weight: 600; }
.pu-pill.is-on { background: var(--ui-ok-soft); color: var(--ui-ok-ink); }
.pu-pill.is-bad { background: var(--ui-warn-soft); color: var(--ui-warn-ink); }
.pu-empty { padding: 16px; color: var(--ui-dim); margin: 0; }
.pu-dlg { background: var(--ui-card); color: var(--ui-ink); border: 1px solid var(--ui-line); border-radius: 12px; }
.pu-dlg__head { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; border-bottom: 1px solid var(--ui-line); font-size: 16px; }
.pu-dlg__head button { font-size: 22px; line-height: 1; color: inherit; background: transparent; border: 0; cursor: pointer; }
.pu-dlg__body { display: flex; flex-direction: column; gap: 12px; padding: 14px 16px; }
.pu-dlg__body label { display: block; margin-bottom: 4px; color: var(--ui-dim); font-size: 12px; }
.pu-dlg__foot { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 16px; border-top: 1px solid var(--ui-line); }
.pu-note { display: block; margin-top: 2px; font-size: 11.5px; color: var(--ui-dim); }
.pu-seg { display: inline-grid; grid-template-columns: 1fr 1fr; width: 220px; border: 1px solid var(--ui-line-strong); border-radius: 8px; overflow: hidden; }
.pu-seg button { padding: 5px 0; font-size: 12.5px; background: transparent; color: inherit; cursor: pointer; }
.pu-seg button.on { background: var(--ui-pri); color: #fff; }
.pu-link { color: var(--ui-pri); text-decoration: none; width: fit-content; }
.pu-link:hover { text-decoration: underline; }
.pu-imei-bad { color: #ef4444; font-weight: 500; }
.pu-warn { margin: 0; padding: 6px 10px; border-radius: 8px; background: var(--ui-warn-soft); color: var(--ui-warn-ink); font-size: 12.5px; }
.pu-err { margin: 0; padding: 6px 10px; border-radius: 8px; background: var(--ui-warn-soft); color: var(--ui-warn-ink); font-size: 12.5px; }
.pu-btn { display: inline-flex; align-items: center; gap: 4px; padding: 6px 14px; border: 1px solid var(--ui-line-strong); border-radius: 6px; font-size: 13px; color: inherit; background: transparent; cursor: pointer; }
.pu-btn:disabled { opacity: 0.5; cursor: default; }
.pu-btn--pri { background: var(--ui-pri); border-color: var(--ui-pri); color: #ffffff; }
.pu-mono-input :deep(input) { font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 12.5px; }
</style>
